import { Spacing } from '@persona-id/design-tokens';
import { CheckIcon } from '@persona-id/design-tokens/icons';
import { groupBy, lowerCase, startCase, upperFirst } from 'lodash/fp';
import { Fragment, ReactNode } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled, { css } from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import ChecksTable from 'dashboard/components/ChecksTable';
import { EXTRACTOR_TYPE_TO_DISPLAY_NAME } from 'dashboard/components/ExtractionPanel/constants';
import {
  INSIGHT_TYPE_TO_DISPLAY_NAME,
  RESPONSE_TYPE_TO_DISPLAY_NAME,
} from 'dashboard/components/InsightPanel/util';
import Pii from 'dashboard/components/Pii';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import {
  DEFAULT_DOCUMENT_GENERIC_CONFIG,
  DocumentGeneric,
  ExtractAction,
  ExtractionRequest,
  ExtractionResponse,
  ExtractionType,
  ExtractorType,
  InsightRequest,
  InsightResponse,
  InsightResponseType,
  InsightType,
  MatchLevel,
  PRESET_EXTRACTOR_TO_DISPLAY_NAME,
} from 'dashboard/models/document';
import { DOCUMENT_TYPE_TO_DISPLAY_NAME } from 'dashboard/models/verification-check-config/document-type-detection';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { FieldsMap, FieldType } from 'lib/fields';

const SUPPORTED_DOCUMENT_FIELD_TYPES = [
  FieldType.Array,
  FieldType.Boolean,
  FieldType.Integer,
  FieldType.Number,
  FieldType.String,
];

const INSIGHT_RESULT_WORD_LIMIT = 250;

const OverviewContainer = styled.div`
  display: grid;
  grid-template-columns: max-content auto;
  grid-column-gap: ${Spacing[5]};
`;

const FilesAndClassification = styled(AttributeGrid)`
  ${({ colCount }) =>
    colCount === 2 &&
    css`
      grid-template-columns: 2fr 1fr;
    `}

  div {
    overflow: hidden;
  }
`;

const Filename = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
`;

// TODO (Documents): Migrate to PDS component for underline tooltip indicators when available
const EllipsisTooltip = styled.span`
  width: fit-content;
  text-decoration: underline;
  text-decoration-style: dotted;
`;

interface EmptyTableProps {
  colSpan: number;
  children: ReactNode;
}

const EmptyTable = ({ colSpan, children }: EmptyTableProps) => (
  <TableRow>
    <TableCell colSpan={colSpan} style={{ textAlign: 'center' }}>
      <Spacer height={1} />
      {children}
      <Spacer height={1} />
    </TableCell>
  </TableRow>
);

interface ExtractionResultsMatchProps {
  value: string;
  matchLevel?: MatchLevel;
  count?: number;
}

const ExtractionResultsMatch = ({
  value,
  matchLevel = MatchLevel.None,
  count = 1,
}: ExtractionResultsMatchProps) => (
  <Paragraph color='primary' style={{ display: 'flex' }}>
    {value}
    {matchLevel !== MatchLevel.None && (
      <>
        <Spacer height={0} width={0.5} />
        <Sticker variant={matchLevel === MatchLevel.Full ? 'success' : 'basic'}>
          {startCase(matchLevel)}
        </Sticker>
      </>
    )}
    {count > 1 && (
      <>
        <Spacer height={0} width={0.5} />
        <Sticker>{count}</Sticker>
      </>
    )}
  </Paragraph>
);

interface ExtractionResultsProps {
  extractions: ExtractionResponse[];
  extractionConfigs: ExtractionRequest[];
  redacted: boolean;
}

const ExtractionResults = ({
  extractions,
  extractionConfigs,
  redacted,
}: ExtractionResultsProps) => (
  <Accordion title='Extractions'>
    <Spacer height={2} />
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Label & type</TableHeaderCell>
          <TableHeaderCell>Search for</TableHeaderCell>
          <TableHeaderCell>Matches found</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {redacted ? (
          <EmptyTable colSpan={3}>
            <Pii isRedacted />
          </EmptyTable>
        ) : extractionConfigs.length === 0 ? (
          <EmptyTable colSpan={3}>No extractions</EmptyTable>
        ) : (
          extractionConfigs.map((config) => {
            const key = config['field-name'];
            const extraction = extractions.find((extraction) => extraction['field-name'] === key);

            let typeLabel = undefined;
            let searchFor = undefined;
            switch (config['extraction-type']) {
              case ExtractionType.Guided:
                typeLabel = EXTRACTOR_TYPE_TO_DISPLAY_NAME[ExtractorType.InquiryFieldComparison];
                searchFor = extraction?.['source-value'] && `"${extraction['source-value']}"`;
                break;
              case ExtractionType.Unguided:
                typeLabel = EXTRACTOR_TYPE_TO_DISPLAY_NAME[config['extractor-type']];

                switch (config['extractor-type']) {
                  case ExtractorType.Ai:
                    searchFor =
                      config['extraction-params'].properties[
                        config['primary-property-key'].replace(/_/g, '-')
                      ].description;
                    break;
                  case ExtractorType.Text:
                    searchFor = config['text-extractors']
                      .filter((extractor) => extractor['extract-action'] === ExtractAction.Accept)
                      .map((extractor, index) => (
                        <Fragment key={extractor['extract-pattern']}>
                          {!!index && <Spacer height={1.5} />}
                          {extractor['extract-pattern']}
                        </Fragment>
                      ));
                    break;
                  case ExtractorType.Regex:
                    searchFor = config['regex-extractors']
                      .filter((extractor) => extractor['extract-action'] === ExtractAction.Accept)
                      .map((extractor, index) => (
                        <Fragment key={extractor['extract-pattern']}>
                          {!!index && <Spacer height={1.5} />}
                          {extractor['extract-name'] ? (
                            <Tooltip
                              trigger={
                                <EllipsisTooltip>{extractor['extract-name']}</EllipsisTooltip>
                              }
                            >
                              {extractor['extract-pattern']}
                            </Tooltip>
                          ) : (
                            extractor['extract-pattern']
                          )}
                        </Fragment>
                      ));
                    break;
                  case ExtractorType.KeyValuePair:
                    searchFor = config['search-keys'].join(', ');
                    break;
                  case ExtractorType.Preset:
                    searchFor = PRESET_EXTRACTOR_TO_DISPLAY_NAME[config['preset-extractor']];
                    break;
                  default:
                    typeLabel = 'Template';
                    searchFor = EXTRACTOR_TYPE_TO_DISPLAY_NAME[config['extractor-type']];
                }
                break;
            }

            const results = groupBy('value', extraction?.results);

            return (
              <TableRow key={key} data-test={`extraction-results__row-${key}`}>
                <TableCell style={{ verticalAlign: 'top' }}>
                  <Paragraph color='primary'>{upperFirst(lowerCase(key))}</Paragraph>
                  <Paragraph>{typeLabel}</Paragraph>
                </TableCell>
                <TableCell style={{ verticalAlign: 'top', whiteSpace: 'normal' }}>
                  {searchFor || <Paragraph>None</Paragraph>}
                </TableCell>
                <TableCell style={{ verticalAlign: 'top' }}>
                  {Object.entries(results).length > 0 ? (
                    Object.entries(results).map(([value, result], index) => (
                      <Fragment key={value}>
                        {!!index && <Spacer height={0.5} />}
                        <ExtractionResultsMatch
                          value={value}
                          matchLevel={
                            extraction?.['extraction-type'] === ExtractionType.Guided &&
                            result.length > 0
                              ? result[0]['match-level']
                              : undefined
                          }
                          count={result.length}
                        />
                      </Fragment>
                    ))
                  ) : (
                    <Paragraph>None</Paragraph>
                  )}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </Accordion>
);

interface InsightResultsMatchProps {
  value: string;
  match: boolean;
}

const InsightResultsMatch = ({ value, match }: InsightResultsMatchProps) => (
  <Paragraph color={match ? 'primary' : 'secondary'} style={{ display: 'flex' }}>
    {value}
    {match && (
      <>
        <Spacer height={0} width={0.5} />
        <Sticker icon={<CheckIcon />} variant='success' />
      </>
    )}
  </Paragraph>
);

interface InsightResultsProps {
  insights: InsightResponse[];
  insightConfigs: InsightRequest[];
  redacted: boolean;
}

const InsightResults = ({ insights, insightConfigs, redacted }: InsightResultsProps) => (
  <Accordion title='Insights'>
    <Spacer height={2} />
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Label & type</TableHeaderCell>
          <TableHeaderCell>Insight</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {redacted ? (
          <EmptyTable colSpan={2}>
            <Pii isRedacted />
          </EmptyTable>
        ) : insightConfigs.length === 0 ? (
          <EmptyTable colSpan={2}>No insights</EmptyTable>
        ) : (
          insightConfigs.map((config) => {
            const key = config.key;
            const insight = insights.find((insight) => insight.key === key);

            let typeLabel = undefined;
            const results = insight?.results;
            let insightResponse = undefined;
            switch (config.type) {
              case InsightType.String:
                typeLabel = INSIGHT_TYPE_TO_DISPLAY_NAME[config.type];
                insightResponse = results?.map(({ value, truncated }) => (
                  <Paragraph key={value} color='primary'>
                    {value}
                    {truncated && (
                      <Tooltip trigger={<EllipsisTooltip>...</EllipsisTooltip>}>
                        Message truncated at {INSIGHT_RESULT_WORD_LIMIT} words
                      </Tooltip>
                    )}
                  </Paragraph>
                ));
                break;
              case InsightType.MultiSelect: {
                typeLabel = `${INSIGHT_TYPE_TO_DISPLAY_NAME[config.type]}: ${RESPONSE_TYPE_TO_DISPLAY_NAME[config['response-type']]}`;
                const defaultResponse = config['default-response'];
                const isDefaultResponse =
                  config['response-type'] === InsightResponseType.Single &&
                  defaultResponse != null &&
                  results &&
                  results.length > 0 &&
                  results[0].value === defaultResponse &&
                  !config.options.some(({ value }) => value === defaultResponse);

                insightResponse = (
                  <>
                    {config.options.map(({ value }, index) => (
                      <Fragment key={value}>
                        {!!index && <Spacer height={0.5} />}
                        <InsightResultsMatch
                          value={value}
                          match={results?.some((result) => result.value === value) || false}
                        />
                      </Fragment>
                    ))}
                    {isDefaultResponse && (
                      <>
                        <Spacer height={0.5} />
                        <InsightResultsMatch value={defaultResponse} match />
                      </>
                    )}
                  </>
                );
                break;
              }
            }

            return (
              <TableRow key={key} data-test={`insight-results__row-${key}`}>
                <TableCell style={{ verticalAlign: 'top' }}>
                  <Paragraph color='primary'>{config.label}</Paragraph>
                  <Paragraph>{typeLabel}</Paragraph>
                </TableCell>
                <TableCell style={{ verticalAlign: 'top', whiteSpace: 'normal' }}>
                  {insightResponse || <Paragraph>None</Paragraph>}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </Accordion>
);

interface DocumentFieldResultsProps {
  documentFields: FieldsMap;
  redacted: boolean;
}

const DocumentFieldResults = ({ documentFields, redacted }: DocumentFieldResultsProps) => (
  <Accordion title='Document Fields'>
    <Spacer height={2} />
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Label & type</TableHeaderCell>
          <TableHeaderCell>Value</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {redacted ? (
          <EmptyTable colSpan={2}>
            <Pii isRedacted />
          </EmptyTable>
        ) : Object.entries(documentFields).length === 0 ? (
          <EmptyTable colSpan={2}>No document fields</EmptyTable>
        ) : (
          Object.entries(documentFields).map(([key, field]) => {
            const typeLabel = startCase(field.type);

            let value = undefined;
            switch (field.type) {
              case FieldType.Array:
                value = field.value
                  .filter(
                    (subField) =>
                      SUPPORTED_DOCUMENT_FIELD_TYPES.includes(subField.type) &&
                      subField.type !== FieldType.Array, // only support one level of nesting
                  )
                  .map((subField, index) => (
                    <Fragment key={index}>
                      {!!index && <Spacer height={1.5} />}
                      {subField.value}
                    </Fragment>
                  ));
                if (value.length === 0) value = undefined;
                break;
              case FieldType.Boolean:
              case FieldType.Integer:
              case FieldType.Number:
              case FieldType.String:
                value = field.value;
                break;
              default:
                value = <Paragraph>Unsupported document field type</Paragraph>;
            }

            return (
              <TableRow key={key} data-test={`document-field-results__row-${key}`}>
                <TableCell style={{ verticalAlign: 'top' }}>
                  <Paragraph color='primary'>{upperFirst(lowerCase(key))}</Paragraph>
                  <Paragraph>{typeLabel}</Paragraph>
                </TableCell>
                <TableCell style={{ verticalAlign: 'top' }}>
                  {value || <Paragraph>None</Paragraph>}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </Accordion>
);

interface DocumentVerificationProps extends VerificationDetailProps {
  document: DocumentGeneric;
}

const DocumentVerification = ({ verification, document }: DocumentVerificationProps) => {
  const { 'redacted-at': redactedAt } = verification.attributes;
  const {
    files,
    'files-normalized': filesNormalized,
    'document-type': classification,
    fields,
  } = document.attributes;
  const extractionResponses = document.attributes['extraction-responses'] || [];
  const insightResponses = document.attributes['insight-responses'] || [];
  const config = document.attributes.config || DEFAULT_DOCUMENT_GENERIC_CONFIG;

  const redacted = redactedAt != null;
  // if the document has not been processed it does not have normalized files
  // we default to using normalized files since some image formats that are not compatible
  // with browsers (HEIC images) have been converted to images
  const displayFiles = files.length === filesNormalized.length ? filesNormalized : files;

  const documentViewerLink = `/dashboard/document-viewer/${document.id}`;

  return (
    <>
      <OverviewContainer data-test='verification-detail__document-verification'>
        <Link to={documentViewerLink} state={{ internalRedirect: true }}>
          <AsyncImage
            value={displayFiles.map((file) => ({
              url: file.url,
              label: file.filename,
              name: file.filename,
              type: file['file-type'],
            }))}
            showCount
            showCountSticker
            width='120px'
            height='160px'
            allowPreviewZoom={false}
          />
        </Link>
        <FilesAndClassification colCount={classification ? 2 : 1}>
          <Attribute
            label='File names'
            value={displayFiles.map(({ filename }, index) => (
              <Filename key={index}>{filename}</Filename>
            ))}
            redacted={redacted}
            placeholder='No document'
          />
          {!!classification && (
            <Attribute
              label='Classification'
              value={
                DOCUMENT_TYPE_TO_DISPLAY_NAME[classification] ||
                upperFirst(lowerCase(classification))
              }
              redacted={redacted}
              placeholder='-'
            />
          )}
        </FilesAndClassification>
      </OverviewContainer>
      <Spacer height={2} />
      <ExtractionResults
        extractions={extractionResponses}
        extractionConfigs={config.extractions}
        redacted={redacted}
      />
      <Spacer height={2} />
      <InsightResults
        insights={insightResponses}
        insightConfigs={config.insights}
        redacted={redacted}
      />
      <Spacer height={2} />
      <DocumentFieldResults documentFields={fields} redacted={redacted} />
      {verification.attributes.checks && (
        <>
          <Spacer height={2} />
          <ChecksTable checks={verification.attributes.checks} />
        </>
      )}
    </>
  );
};

export default DocumentVerification;
